import {
  EnvironmentProviders,
  Injectable,
  InjectionToken,
  NgZone,
  PendingTasks,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, User, getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
  Query,
  QuerySnapshot,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
} from 'firebase/firestore';
import { FirebaseStorage, getStorage } from 'firebase/storage';
import { Observable, map } from 'rxjs';

export const FIREBASE_OPTIONS = new InjectionToken<FirebaseOptions>('FIREBASE_OPTIONS');

export function provideFirebase(options: FirebaseOptions): EnvironmentProviders {
  return makeEnvironmentProviders([{ provide: FIREBASE_OPTIONS, useValue: options }]);
}

interface IdFieldOptions {
  idField?: string;
}

/**
 * Point d'accès unique au SDK Firebase (remplace @angular/fire).
 *
 * - Les écoutes temps réel tournent hors de la zone Angular, les émissions y sont
 *   ramenées pour déclencher la détection de changements.
 * - Le rendu serveur (SSR / prérendu) attend la première réponse Firebase grâce à
 *   PendingTasks : sans cela, la page serait sérialisée avant l'arrivée des données.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private readonly ngZone = inject(NgZone);
  private readonly pendingTasks = inject(PendingTasks);

  private readonly app: FirebaseApp = this.outsideZone(() => this.initApp(inject(FIREBASE_OPTIONS)));

  readonly firestore: Firestore = this.outsideZone(() => getFirestore(this.app));
  readonly auth: Auth = this.outsideZone(() => getAuth(this.app));
  readonly storage: FirebaseStorage = this.outsideZone(() => getStorage(this.app));

  authState(): Observable<User | null> {
    return this.listen<User | null>((next, error) => onAuthStateChanged(this.auth, next, error));
  }

  collectionData<T = DocumentData>(query: Query<DocumentData>, options: IdFieldOptions = {}): Observable<T[]> {
    return this.listen<QuerySnapshot<DocumentData>>((next, error) => onSnapshot(query, next, error)).pipe(
      map((snapshot) => snapshot.docs.map((d) => this.withId(d.data(), d.id, options.idField) as T))
    );
  }

  docData<T = DocumentData>(ref: DocumentReference<DocumentData>, options: IdFieldOptions = {}): Observable<T | undefined> {
    return this.listen<DocumentSnapshot<DocumentData>>((next, error) => onSnapshot(ref, next, error)).pipe(
      map((snapshot) =>
        snapshot.exists() ? (this.withId(snapshot.data(), snapshot.id, options.idField) as T) : undefined
      )
    );
  }

  getDoc<T = DocumentData>(ref: DocumentReference<T>): Promise<DocumentSnapshot<T>> {
    return this.track(() => getDoc(ref));
  }

  getDocs<T = DocumentData>(query: Query<T>): Promise<QuerySnapshot<T>> {
    return this.track(() => getDocs(query));
  }

  private initApp(options: FirebaseOptions): FirebaseApp {
    return getApps().length ? getApp() : initializeApp(options);
  }

  private withId(data: DocumentData, id: string, idField?: string): DocumentData {
    return idField ? { ...data, [idField]: id } : data;
  }

  private outsideZone<T>(fn: () => T): T {
    return this.ngZone.runOutsideAngular(fn);
  }

  /**
   * Lance l'appel Firebase hors zone Angular (ses minuteurs internes empêcheraient
   * l'application de devenir stable) et ramène le résultat dans la zone.
   */
  private track<T>(start: () => Promise<T>): Promise<T> {
    const done = this.pendingTasks.add();
    return new Promise<T>((resolve, reject) => {
      this.outsideZone(start).then(
        (value) => this.ngZone.run(() => resolve(value)),
        (err) => this.ngZone.run(() => reject(err))
      ).finally(done);
    });
  }

  private listen<T>(
    subscribe: (next: (value: T) => void, error: (err: Error) => void) => () => void
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      let done: (() => void) | undefined = this.pendingTasks.add();
      const release = () => {
        done?.();
        done = undefined;
      };

      const unsubscribe = this.ngZone.runOutsideAngular(() =>
        subscribe(
          (value) =>
            this.ngZone.run(() => {
              subscriber.next(value);
              release();
            }),
          (err) =>
            this.ngZone.run(() => {
              subscriber.error(err);
              release();
            })
        )
      );

      return () => {
        release();
        unsubscribe();
      };
    });
  }
}
